// The annual reports of a company -> one row for each fiscal year.
//
// The year is the year of `report.period.endDate`. On :4902, `endDate` is the
// midnight UTC of that same day (1443571200000 = 2015-09-30). The old site moved
// a year that ends on 31 December to the next year. That labelled the fiscal
// year 2022 as "2023", so this module does not do it.
//
// A zero shows as no value. The old site did the same, because a report often
// holds 0 for a line that it does not state. The exception: when every amount of
// the report is 0, each 0 shows as 0.

// These forms file no annual report, so the page does not ask for one.
const NO_REPORTS = new Set(['ENK', 'PMV', 'I/S', 'FON', 'FFO']);

export function hasFinancials(form) {
  return !NO_REPORTS.has(form);
}

function amount(value) {
  return typeof value === 'number' && Number.isFinite(value) && value !== 0 ? value : null;
}

// A report where every stated amount is 0 shows each 0 (owner 2026-09-15). A row of
// blank cells looked like data that the site lost (enhedsNummer 4009342031).
function allZero(values) {
  const stated = values.filter((value) => typeof value === 'number' && Number.isFinite(value));
  return stated.length > 0 && stated.every((value) => value === 0);
}

function zeroOrNull(value) {
  return value === 0 ? 0 : null;
}

/** Rows from cvrfinancial -> fiscal years, newest first. The same period twice gives one row. */
export function financialYears(rows) {
  const seen = new Set();
  const years = [];
  for (const row of rows ?? []) {
    const report = row?.report;
    const income = report?.incomeStatement;
    if (!income) continue;

    const start = report.period?.startDate ?? row.start ?? null;
    const end = report.period?.endDate ?? (row.endDate ? String(row.endDate).slice(0, 10) : null);
    const key = `${start}|${end}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const equity = report.balance?.liabilitiesAndEquity?.equity ?? {};
    const equityTotal = equity.total
      ?? (equity.contributedCapital == null && equity.retainedEarnings == null
        ? null
        : (equity.contributedCapital ?? 0) + (equity.retainedEarnings ?? 0));
    const values = {
      grossProfit: income.grossProfitLoss,
      employeeExpenses: income.employeeExpenses,
      profitLoss: income.profitLoss,
      equity: equityTotal,
      contributedCapital: equity.contributedCapital,
      employees: report.otherReportingData?.averageNumberOfEmployees,
    };
    const shown = allZero(Object.values(values)) ? zeroOrNull : amount;
    const year = { year: end ? Number(end.slice(0, 4)) : null, start, end };
    for (const [field, value] of Object.entries(values)) year[field] = shown(value);
    years.push(year);
  }
  return years.sort((a, b) => String(b.end ?? '').localeCompare(String(a.end ?? '')));
}
